import { type Session, type User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { createContext, type ReactNode, useCallback, useContext } from 'react';

import { useToast } from '@/hooks/use-toast';
import { slackSignOut } from '@/lib/queries/slackAuth';
import { supabaseClient } from '@/lib/supabase';

type AuthenticatedUser = {
  user: User;
  session: Session;
  signOut: () => void;
};

const AuthenticatedUserContext = createContext<AuthenticatedUser | null>(null);
export const AuthenticatedUserProvider = ({
  children,
  user,
  session,
}: {
  children: ReactNode;
  user: User;
  session: Session;
}): JSX.Element => {
  const router = useRouter();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const signOut = useCallback(async () => {
    await slackSignOut();
    const { error } = await supabaseClient.auth.signOut();

    if (error) {
      toast({
        variant: 'destructive',
        title: 'global.menu.signOutError',
      });

      throw error;
    }

    queryClient.clear();
    router.navigate({
      to: '/sign-in',
    });
  }, [router, toast, queryClient]);

  return (
    <AuthenticatedUserContext.Provider
      value={{
        user,
        session,
        signOut,
      }}
    >
      {children}
    </AuthenticatedUserContext.Provider>
  );
};

export const useAuthenticatedUser = (): AuthenticatedUser => {
  const authenticatedUser = useContext(AuthenticatedUserContext);
  if (authenticatedUser === null) {
    throw new Error(
      'useAuthenticatedUser must be used within an AuthenticatedUserProvider',
    );
  }

  return authenticatedUser;
};
